import React, { useContext, useState } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API_URL, API_KEY } from "../API";
import { AuthContext } from "../Context/AuthContext";
import Loader from "./Loader";
import NoData from "./NoData";
import "../style/Wishlist.css";
import "../style/Categories.css";

const fetchWishlist = async (token) => {
  const response = await axios.get(`${API_URL}/favorite/`, {
    headers: {
      "x-api-key": API_KEY,
      Authorization: `Bearer ${token}`,
      "accept-language": "ar",
    },
  });
  return response.data.data.favorites.favorites;
};

const WishlistPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { token } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loadingId, setLoadingId] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["wishlist"],
    queryFn: () => fetchWishlist(token),
    staleTime: 1000 * 60 * 5,
    enabled: !!token,
  });

  const removeFavorite = useMutation({
    mutationFn: async (id) => {
      setLoadingId(id);
      await axios.delete(`${API_URL}/favorite/remove`, {
        data: { productIds: [id] },
        headers: {
          "x-api-key": API_KEY,
          Authorization: `Bearer ${token}`,
        },
      });
    },
    onSuccess: (_, id) => {
      enqueueSnackbar(t("product.removedFromFavorites"), {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      queryClient.setQueryData(["wishlist"], (old) =>
        Array.isArray(old) ? old.filter((it) => it.product?.id !== id) : old
      );
      setLoadingId(null);
    },
    onError: () => {
      enqueueSnackbar(t("product.removeError"), { variant: "error" });
      setLoadingId(null);
    },
  });

  if (isLoading) return <Loader />;
  if (error) {
    return <div className="error">❌ {t("cart.loadErrorTitle")}</div>;
  }
  if (!Array.isArray(data) || data.length === 0) {
    return <NoData />;
  }

  return (
    <div className="home-cat wishlist-grid">
      {data.map((item) => {
        const product = item.product;
        if (!product) return null;

        const name =
          product.name || product.translations?.[0]?.name || t("unnamed_product");
        const imageSrc = product.image?.startsWith("http")
          ? product.image
          : "/images/placeholder.png";

        const discount = product.discount_price;
        const originalPrice = product.price;
        const discountedPrice =
          discount && !isNaN(discount)
            ? originalPrice - (originalPrice * discount) / 100
            : null;

        return (
          <div
            key={product.id}
            className="fav-card"
            onClick={() => navigate(`/product-details/${product.id}`)}
          >
            <button
              className="fav-remove"
              title={t("product.removedFromFavorites")}
              onClick={(e) => {
                e.stopPropagation();
                if (loadingId === product.id) return;
                removeFavorite.mutate(product.id);
              }}
            >
              {loadingId === product.id ? (
                <span className="loader-small"></span>
              ) : (
                <img src="/images/heart1.svg" alt="remove favorite" />
              )}
            </button>

            <div className="middel">
              <img
                src={imageSrc}
                alt={name}
                onError={(e) => (e.target.src = "/images/placeholder.png")}
              />
            </div>

            <div className="buttom">
              {discountedPrice ? (
                <div className="fav-price-row">
                  <h3>
                    {discountedPrice.toLocaleString()} {t("currency_sp")}
                  </h3>
                  <span className="fav-old-price">
                    {originalPrice.toLocaleString()} {t("currency_sp")}
                  </span>
                </div>
              ) : (
                <h3>
                  {originalPrice?.toLocaleString()} {t("currency_sp")}
                </h3>
              )}
              <p className="fav-name">{name}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default WishlistPage;
